import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

/**
 * The collapsible dealer table that sits under the map on both dashboards.
 *
 * Collapsed on load, as the Razor pages were. The chevron and the panel body are
 * the same markup on both screens; only the grid inside them differs, so that is
 * the only thing passed in.
 *
 * The header is a real `<button>`, not the `<div>` with a click handler the Razor
 * version used, so the panel can be opened from the keyboard.
 */
export interface DealersPanelProps {
  open: boolean;
  onToggle: () => void;
  /** Toolbar row above the grid — search box, export button. */
  toolbar?: ReactNode;
  children: ReactNode;
}

export function DealersPanel({ open, onToggle, toolbar, children }: DealersPanelProps) {
  const { t } = useTranslation('mystery/dealerlist');

  return (
    <div className="regional-dealers-panel">
      <button
        type="button"
        className="regional-dealers-panel-header"
        onClick={onToggle}
        aria-expanded={open}
        aria-controls="dealersPanelContent"
      >
        <h3 className="regional-dealers-panel-title">
          {t('Regional.DealersPanel.Title', 'Dealers List')}
        </h3>
        <span
          className={`regional-dealers-panel-toggle${open ? ' expanded' : ''}`}
          aria-hidden="true"
        >
          ▾
        </span>
      </button>

      <div
        id="dealersPanelContent"
        className={`regional-dealers-panel-content${open ? ' show' : ''}`}
      >
        {toolbar ? <div className="regional-dealers-toolbar">{toolbar}</div> : null}
        {children}
      </div>
    </div>
  );
}
